"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeMonthlyGeneration = computeMonthlyGeneration;
exports.deriveFederalStatusFromGeneration = deriveFederalStatusFromGeneration;
exports.deriveStateStatusFromGeneration = deriveStateStatusFromGeneration;
const browser_1 = require("encamp-prisma/generated/browser");
const wasteGeneratorStatusThresholds_1 = require("encamp-shared/dist/constants/wasteGeneratorStatusThresholds");
const unitConversions_1 = require("encamp-shared/dist/units/unitConversions");
/**
 * A code marks waste oil when it ends in `MA01` (mirrors the shipment SQL
 * `code ~ 'MA01$'`).
 *
 * Matched case-insensitively on purpose. `WasteStreamRaw.stateWasteCodes` is
 * written by `normalizeStateWasteCode`, which preserves the source's case, so a
 * vendor feed that sends `ma01` persists lowercase. A case-sensitive test would
 * silently skip the exclusion for those streams — and silently is the bad kind:
 * the facility would look like a higher-tier generator with no signal why. (The
 * shipment SQL's `~ 'MA01$'` has the same blind spot; fixing it there is a
 * separate change to a query this PR doesn't touch.)
 */
function streamHasWasteOilCode(stateWasteCodes) {
    return stateWasteCodes.some((code) => code.trim().toUpperCase().endsWith("MA01"));
}
/**
 * A code marks TX Class-1 when it ends in `1` (mirrors the shipment SQL
 * `code ~ '.*1$'`). Trimmed for the same reason as the waste-oil check; case is
 * irrelevant to a digit.
 */
function streamHasClass1Code(stateWasteCodes) {
    return stateWasteCodes.some((code) => code.trim().endsWith("1"));
}
/** UTC first-of-month for `month`. Matches the `@db.Date` periodStart storage. */
function monthStartUtc(month) {
    return new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth(), 1));
}
/** UTC first-of-the-next-month — the exclusive end of the month window. */
function nextMonthStartUtc(month) {
    return new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth() + 1, 1));
}
function emptyGenerationMasses() {
    return {
        hazardousKg: 0,
        acuteKg: 0,
        stateRegulatedKg: 0,
        stateRegulatedExcludingWasteOilKg: 0,
        stateRegulatedExcludingClass1Kg: 0,
    };
}
/**
 * Sums a facility's positive `ContainerFillEvent` mass for one calendar month,
 * split into RCRA acute vs non-acute by the container's `WasteStream`
 * classification.
 *
 * Generation is by definition the positive ContainerFillEvents; we are
 * intentionally not including the negative ones, because that would give
 * net amount on-site, which is just a different thing that is handled elsewhere.
 *
 * Only fills that count toward generation are summed. RCRA fills feed the
 * federal split and `STATE_REGULATED` fills feed the state totals.
 *
 * `fills` must already be scoped to the target facility; the caller owns that
 * scoping (the facility never appears in the arithmetic).
 */
function computeMonthlyGeneration(month, fills) {
    const start = monthStartUtc(month).getTime();
    const end = nextMonthStartUtc(month).getTime();
    const generatorStatusEligible = emptyGenerationMasses();
    const episodic = emptyGenerationMasses();
    let fillEventCount = 0;
    const incompleteFills = [];
    for (const fill of fills) {
        // Each fill lands in exactly one scope; the full-site totals are the two
        // added back together at the end.
        const scope = fill.isEpisodic ? episodic : generatorStatusEligible;
        // Only positive ADDs are generation; removals/transfers/empties are not.
        if (fill.amount <= 0)
            continue;
        // A consolidation transfer-in re-adds already-counted waste on the
        // receiving container — exclude it so consolidation can't inflate the month.
        if (fill.sourceContainerId !== null)
            continue;
        const occurred = fill.occurredAt.getTime();
        if (occurred < start || occurred >= end)
            continue;
        const stream = fill.container.wasteStream;
        const kg = (0, unitConversions_1.convertAmount)(fill.amount, fill.unit, browser_1.UnitType.KILOGRAMS, stream?.profile?.specificGravity, { defaultToWater: true }).amount;
        if (kg === null) {
            // will never happen because convertAmount never returns null with `defaultToWater: true`
            // but we don't have a compile-time guarantee for that
            throw new Error(`Could not convert amount to kg`);
        }
        if (kg <= 0)
            continue;
        // A container with no stream counts as non-acute RCRA hazardous: acute
        // would be more conservative, but would raise alarms for a case that is
        // very unlikely in reality.
        if (stream !== null && !stream.countsTowardGeneration)
            continue;
        // State-regulated (non-RCRA) waste feeds STATE generator status only; it is
        // never part of the federal split. Summed here — with the MA/TX code-based
        // deltas — so the writer can run the state calculators.
        if (stream?.classification === browser_1.WasteClassification.STATE_REGULATED) {
            scope.stateRegulatedKg += kg;
            const stateWasteCodes = stream.rawWasteStreams.flatMap((raw) => raw.stateWasteCodes);
            if (!streamHasWasteOilCode(stateWasteCodes)) {
                scope.stateRegulatedExcludingWasteOilKg += kg;
            }
            if (!streamHasClass1Code(stateWasteCodes)) {
                scope.stateRegulatedExcludingClass1Kg += kg;
            }
            continue;
        }
        if (stream?.classification === browser_1.WasteClassification.RCRA_ACUTELY_HAZARDOUS) {
            scope.acuteKg += kg;
        }
        else {
            scope.hazardousKg += kg;
        }
        fillEventCount += 1;
    }
    const totals = [generatorStatusEligible, episodic].reduce((acc, cur) => ({
        acuteKg: acc.acuteKg + cur.acuteKg,
        hazardousKg: acc.hazardousKg + cur.hazardousKg,
        stateRegulatedExcludingClass1Kg: acc.stateRegulatedExcludingClass1Kg + cur.stateRegulatedExcludingClass1Kg,
        stateRegulatedExcludingWasteOilKg: acc.stateRegulatedExcludingWasteOilKg + cur.stateRegulatedExcludingWasteOilKg,
        stateRegulatedKg: acc.stateRegulatedKg + cur.stateRegulatedKg,
    }), emptyGenerationMasses());
    return {
        ...totals,
        generatorStatusEligible,
        episodic,
        incompleteFills,
        fillEventCount,
    };
}
/**
 * Returns null when nothing was generated to signal "we don't know"
 *
 * This function is unaware of episodic exemptions - pass it the
 * generated amount EXCLUDING episodic
 */
function deriveFederalStatusFromGeneration(generation) {
    const { hazardousKg, acuteKg } = generation;
    if (hazardousKg <= 0 && acuteKg <= 0)
        return null;
    if ((0, wasteGeneratorStatusThresholds_1.kilogramsExceed)(acuteKg, wasteGeneratorStatusThresholds_1.LQG_THRESHOLD_ACUTE_HAZARDOUS_KG)) {
        return browser_1.FederalRegulatoryStatus.LARGE_QUANTITY_GENERATOR;
    }
    if ((0, wasteGeneratorStatusThresholds_1.kilogramsReach)(hazardousKg, wasteGeneratorStatusThresholds_1.LQG_THRESHOLD_HAZARDOUS_KG)) {
        return browser_1.FederalRegulatoryStatus.LARGE_QUANTITY_GENERATOR;
    }
    if ((0, wasteGeneratorStatusThresholds_1.kilogramsExceed)(hazardousKg, wasteGeneratorStatusThresholds_1.SQG_THRESHOLD_HAZARDOUS_KG)) {
        return browser_1.FederalRegulatoryStatus.SMALL_QUANTITY_GENERATOR;
    }
    return browser_1.FederalRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR;
}
/**
 * State generator status for a state that has no rules of its own beyond
 * counting state-regulated (non-RCRA) waste against the federal thresholds.
 *
 * States WITH their own calculator (`stateCalculatorMap`) keep running the shared
 * lb calculators — their rules genuinely differ from federal, so the two axes are
 * expected to differ there.
 */
function deriveStateStatusFromGeneration(generation) {
    const { acuteKg, stateRegulatedKg } = generation;
    const nonAcuteKg = generation.hazardousKg + stateRegulatedKg;
    if (nonAcuteKg <= 0 && acuteKg <= 0)
        return null;
    if ((0, wasteGeneratorStatusThresholds_1.kilogramsExceed)(acuteKg, wasteGeneratorStatusThresholds_1.LQG_THRESHOLD_ACUTE_HAZARDOUS_KG)) {
        return browser_1.StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR;
    }
    if ((0, wasteGeneratorStatusThresholds_1.kilogramsReach)(nonAcuteKg, wasteGeneratorStatusThresholds_1.LQG_THRESHOLD_HAZARDOUS_KG)) {
        return browser_1.StateRegulatoryStatus.LARGE_QUANTITY_GENERATOR;
    }
    if ((0, wasteGeneratorStatusThresholds_1.kilogramsExceed)(nonAcuteKg, wasteGeneratorStatusThresholds_1.SQG_THRESHOLD_HAZARDOUS_KG)) {
        return browser_1.StateRegulatoryStatus.SMALL_QUANTITY_GENERATOR;
    }
    return browser_1.StateRegulatoryStatus.VERY_SMALL_QUANTITY_GENERATOR;
}
//# sourceMappingURL=generation.js.map